import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Stepper,
  Step,
  StepLabel,
  LinearProgress,
  Card,
  CardContent,
  Grid,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  FormControlLabel,
  Switch,
  Divider
} from '@mui/material';
import WorkflowMonitor from '../components/WorkflowMonitor';
import {
  PlayArrow as PlayIcon,
  Visibility as ViewIcon,
  Cancel as CancelIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  HourglassEmpty as PendingIcon,
  TrendingFlat as RunningIcon
} from '@mui/icons-material';
import axios from 'axios';
import { format } from 'date-fns';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

interface Workflow {
  id: string;
  workspace_id: string;
  name: string;
  goal: string;
  status: 'pending' | 'running' | 'completed' | 'failed';
  steps: any[];
  created_at: string;
  updated_at: string;
}

interface TaskExecution {
  id: string;
  workflow_id: string;
  agent_name: string;
  task_type: string;
  status: 'pending' | 'running' | 'completed' | 'failed';
  input: any;
  output: any;
  started_at: string | null;
  completed_at: string | null;
}

const statusColors: Record<string, 'default' | 'primary' | 'success' | 'error'> = {
  pending: 'default',
  running: 'primary',
  completed: 'success',
  failed: 'error'
};

const statusIcons: Record<string, React.ReactElement> = {
  pending: <PendingIcon />,
  running: <RunningIcon />,
  completed: <CheckCircleIcon />,
  failed: <ErrorIcon />
};

export default function Workflows() {
  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedWorkflow, setSelectedWorkflow] = useState<string | null>(null);
  const [workflowDetails, setWorkflowDetails] = useState<any | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newWorkflowGoal, setNewWorkflowGoal] = useState('');
  const [useSaga, setUseSaga] = useState(true);

  useEffect(() => {
    loadWorkflows();
    // Auto-refresh every 5 seconds
    const interval = setInterval(loadWorkflows, 5000);
    return () => clearInterval(interval);
  }, []);

  const loadWorkflows = async () => {
    try {
      const response = await axios.get(`${API_URL}/workflows`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      setWorkflows(response.data);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load workflows');
    } finally {
      setLoading(false);
    }
  };

  const createWorkflow = async () => {
    if (!newWorkflowGoal.trim()) return;

    try {
      await axios.post(
        `${API_URL}/workflows`,
        { goal: newWorkflowGoal, context: {} },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('access_token')}`
          }
        }
      );
      setCreateDialogOpen(false);
      setNewWorkflowGoal('');
      loadWorkflows();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to create workflow');
    }
  };

  const executeWorkflow = async (workflowId: string) => {
    try {
      await axios.post(
        `${API_URL}/workflows/${workflowId}/execute?use_saga=${useSaga}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('access_token')}`
          }
        }
      );
      loadWorkflows();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to execute workflow');
    }
  };

  const viewWorkflowDetails = async (workflowId: string) => {
    try {
      const response = await axios.get(
        `${API_URL}/workflows/${workflowId}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('access_token')}`
          }
        }
      );
      setWorkflowDetails(response.data);
      setSelectedWorkflow(workflowId);
      setDetailsOpen(true);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load workflow details');
    }
  };

  const cancelWorkflow = async (workflowId: string) => {
    try {
      await axios.delete(
        `${API_URL}/workflows/${workflowId}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('access_token')}`
          }
        }
      );
      loadWorkflows();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to cancel workflow');
    }
  };

  const getProgressPercentage = (workflow: Workflow): number => {
    if (!workflowDetails || workflowDetails.id !== workflow.id) return 0;
    const tasks = workflowDetails.task_executions || [];
    if (tasks.length === 0) return 0;
    const completed = tasks.filter((t: TaskExecution) => t.status === 'completed').length;
    return Math.round((completed / tasks.length) * 100);
  };

  if (loading && workflows.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 0 } }}>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: { xs: 'flex-start', sm: 'center' },
        mb: { xs: 2, sm: 3 },
        flexDirection: { xs: 'column', sm: 'row' },
        gap: { xs: 2, sm: 0 },
        width: '100%'
      }}>
        <Typography variant="h4" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>Multi-Agent Workflows</Typography>
        <Box sx={{ 
          display: 'flex', 
          gap: 2,
          flexDirection: { xs: 'column', sm: 'row' },
          width: { xs: '100%', sm: 'auto' }
        }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadWorkflows}
            fullWidth={window.innerWidth < 600}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            onClick={() => setCreateDialogOpen(true)}
            fullWidth={window.innerWidth < 600}
          >
            Create Workflow
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Workflows Overview */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Workflows
              </Typography>
              <Typography variant="h4">{workflows.length}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Running
              </Typography>
              <Typography variant="h4" color="primary">
                {workflows.filter(w => w.status === 'running').length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Completed
              </Typography>
              <Typography variant="h4" color="success.main">
                {workflows.filter(w => w.status === 'completed').length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Failed
              </Typography>
              <Typography variant="h4" color="error">
                {workflows.filter(w => w.status === 'failed').length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Workflows Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Goal</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Updated</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {workflows.map((workflow) => (
              <TableRow key={workflow.id} hover>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {workflow.goal}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {workflow.name}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    icon={statusIcons[workflow.status]}
                    label={workflow.status.toUpperCase()}
                    color={statusColors[workflow.status]}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  {format(new Date(workflow.created_at), 'MMM d, yyyy HH:mm')}
                </TableCell>
                <TableCell>
                  {format(new Date(workflow.updated_at), 'MMM d, yyyy HH:mm')}
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="View Details">
                    <IconButton
                      size="small"
                      onClick={() => viewWorkflowDetails(workflow.id)}
                    >
                      <ViewIcon />
                    </IconButton>
                  </Tooltip>
                  {workflow.status === 'pending' && (
                    <Tooltip title="Execute">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => executeWorkflow(workflow.id)}
                      >
                        <PlayIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                  {(workflow.status === 'pending' || workflow.status === 'running') && (
                    <Tooltip title="Cancel">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => cancelWorkflow(workflow.id)}
                      >
                        <CancelIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {workflows.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography color="textSecondary" sx={{ py: 3 }}>
                    No workflows yet. Create your first multi-agent workflow!
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create Workflow Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Workflow</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Workflow Goal"
            fullWidth
            multiline
            rows={3}
            value={newWorkflowGoal}
            onChange={(e) => setNewWorkflowGoal(e.target.value)}
            placeholder="e.g., 'Generate 100 qualified SaaS leads in the marketing automation space'"
            helperText="Describe what you want to accomplish. The orchestrator will break it down into tasks."
          />
          <Box sx={{ mt: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={useSaga}
                  onChange={(e) => setUseSaga(e.target.checked)}
                  color="primary"
                />
              }
              label="Enable Saga Mode (Automatic Rollback)"
            />
            <Typography variant="caption" color="textSecondary" display="block" sx={{ ml: 4, mt: 0.5 }}>
              When enabled, failed tasks will automatically trigger compensation actions to maintain data consistency.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button onClick={createWorkflow} variant="contained" disabled={!newWorkflowGoal.trim()}>
            Create & Plan
          </Button>
        </DialogActions>
      </Dialog>

      {/* Workflow Details Dialog */}
      <Dialog
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Workflow Details
          {workflowDetails && (
            <Chip
              icon={statusIcons[workflowDetails.status]}
              label={workflowDetails.status.toUpperCase()}
              color={statusColors[workflowDetails.status]}
              size="small"
              sx={{ ml: 2 }}
            />
          )}
        </DialogTitle>
        <DialogContent>
          {workflowDetails && (
            <Box>
              <Typography variant="h6" gutterBottom>
                {workflowDetails.goal}
              </Typography>

              {/* Real-Time Monitoring Section */}
              {(workflowDetails.status === 'running' || workflowDetails.status === 'completed' || workflowDetails.status === 'failed') && selectedWorkflow && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                    Real-Time Execution Monitor
                  </Typography>
                  <WorkflowMonitor workflowId={selectedWorkflow} />
                  <Divider sx={{ my: 3 }} />
                </Box>
              )}
              {workflowDetails.task_executions && workflowDetails.task_executions.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">Progress</Typography>
                    <Typography variant="body2">
                      {workflowDetails.task_executions.filter((t: TaskExecution) => t.status === 'completed').length} / {workflowDetails.task_executions.length} tasks
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={getProgressPercentage(workflowDetails)}
                    sx={{ height: 8, borderRadius: 4 }}
                  />
                </Box>
              )}

              {/* Task Executions */}
              <Typography variant="subtitle1" sx={{ mb: 2, mt: 3 }}>
                Task Executions
              </Typography>
              {workflowDetails.task_executions && workflowDetails.task_executions.length > 0 ? (
                workflowDetails.task_executions.map((task: TaskExecution, index: number) => (
                  <Accordion key={task.id}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                        <Chip
                          icon={statusIcons[task.status]}
                          label={task.status}
                          color={statusColors[task.status]}
                          size="small"
                        />
                        <Typography variant="body2">
                          <strong>{task.agent_name}</strong> - {task.task_type}
                        </Typography>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box>
                        {task.input && (
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="caption" color="textSecondary">
                              Input:
                            </Typography>
                            <Paper sx={{ p: 1, mt: 0.5, bgcolor: 'grey.50' }}>
                              <pre style={{ margin: 0, fontSize: '0.75rem', overflow: 'auto' }}>
                                {JSON.stringify(task.input, null, 2)}
                              </pre>
                            </Paper>
                          </Box>
                        )}
                        {task.output && (
                          <Box>
                            <Typography variant="caption" color="textSecondary">
                              Output:
                            </Typography>
                            <Paper sx={{ p: 1, mt: 0.5, bgcolor: 'grey.50' }}>
                              <pre style={{ margin: 0, fontSize: '0.75rem', overflow: 'auto' }}>
                                {JSON.stringify(task.output, null, 2)}
                              </pre>
                            </Paper>
                          </Box>
                        )}
                        {task.started_at && (
                          <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 1 }}>
                            Started: {format(new Date(task.started_at), 'MMM d, yyyy HH:mm:ss')}
                          </Typography>
                        )}
                        {task.completed_at && (
                          <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
                            Completed: {format(new Date(task.completed_at), 'MMM d, yyyy HH:mm:ss')}
                          </Typography>
                        )}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                ))
              ) : (
                <Typography color="textSecondary">
                  No tasks executed yet. Click "Execute" to start this workflow.
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {workflowDetails && workflowDetails.status === 'pending' && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mr: 'auto' }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={useSaga}
                    onChange={(e) => setUseSaga(e.target.checked)}
                    color="primary"
                    size="small"
                  />
                }
                label="Saga Mode"
              />
              <Button
                onClick={() => {
                  executeWorkflow(workflowDetails.id);
                  setDetailsOpen(false);
                }}
                color="primary"
                variant="contained"
                startIcon={<PlayIcon />}
              >
                Execute Workflow
              </Button>
            </Box>
          )}
          <Button onClick={() => setDetailsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
